// src/pages/calendar/bookings/BookingDialogs.js
import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";

// Styles for the calendar-based reschedule dialog (similar to Preview.js)
const styles = {
  dialogContent: {
    padding: 0,
    overflow: "hidden",
  },
  flexRow: {
    display: "flex",
    flexDirection: "row",
    minHeight: "500px",
  },
  calendarSection: {
    flex: "1 1 60%",
    padding: "24px",
    borderRight: "1px solid #e5e7eb",
  },
  timeSlotsSection: {
    flex: "1 1 40%",
    padding: "24px",
    backgroundColor: "#fafafa",
    overflowY: "auto",
    maxHeight: "500px",
  },
  monthHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  monthTitle: {
    color: "#111827",
    fontSize: "16px",
    fontWeight: "500",
    margin: 0,
  },
  navButton: {
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    color: "#6b7280",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
  },
  weekdaysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
    textAlign: "center",
  },
  weekday: {
    color: "#6b7280",
    fontSize: "12px",
    fontWeight: "500",
    padding: "8px 0",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
  },
  dayCell: {
    height: "40px",
    position: "relative",
  },
  dayButton: {
    width: "100%",
    height: "100%",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    border: "none",
    background: "transparent",
    color: "#111827",
    cursor: "pointer",
  },
  dayButtonSelected: {
    backgroundColor: "#3b82f6",
    color: "#ffffff",
  },
  dayButtonToday: {
    border: "2px solid #3b82f6",
    fontWeight: "bold",
  },
  dayButtonUnavailable: {
    color: "#d1d5db",
    cursor: "not-allowed",
  },
  timelineHeader: {
    marginBottom: "16px",
    fontSize: "16px",
    fontWeight: "500",
    color: "#111827",
  },
  timeSlotsContainer: {
    maxHeight: "380px",
    overflowY: "auto",
  },
  timeSlotsList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  timeSlotButton: {
    width: "100%",
    padding: "12px 16px",
    textAlign: "center",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    color: "#111827",
    fontSize: "14px",
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  timeSlotButtonSelected: {
    backgroundColor: "#3b82f6",
    color: "#ffffff",
    border: "1px solid #3b82f6",
  },
  noSlots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "200px",
    color: "#9ca3af",
    flexDirection: "column",
  },
};

// Generate time slots based on availability - EXACT COPY FROM Preview.js
function generateTimeSlots(timeStart, timeEnd, interval = 30) {
  const slots = [];
  let startTime;
  let endTime;

  // Handle 12-hour format strings like "09:00 am" or "06:00 pm"
  if (
    typeof timeStart === "string" &&
    (timeStart.includes("am") || timeStart.includes("pm"))
  ) {
    const [timePart, meridiem] = timeStart.split(" ");
    const [hours, minutes] = timePart.split(":").map(Number);
    let hour24 = hours;

    if (meridiem.toLowerCase() === "pm" && hours < 12) {
      hour24 = hours + 12;
    } else if (meridiem.toLowerCase() === "am" && hours === 12) {
      hour24 = 0;
    }

    startTime = dayjs().hour(hour24).minute(minutes).second(0);
  } else {
    startTime = dayjs(`2000-01-01 ${timeStart}`);
  }

  if (
    typeof timeEnd === "string" &&
    (timeEnd.includes("am") || timeEnd.includes("pm"))
  ) {
    const [timePart, meridiem] = timeEnd.split(" ");
    const [hours, minutes] = timePart.split(":").map(Number);
    let hour24 = hours;

    if (meridiem.toLowerCase() === "pm" && hours < 12) {
      hour24 = hours + 12;
    } else if (meridiem.toLowerCase() === "am" && hours === 12) {
      hour24 = 0;
    }

    endTime = dayjs().hour(hour24).minute(minutes).second(0);
  } else {
    endTime = dayjs(`2000-01-01 ${timeEnd}`);
  }

  let currentTime = startTime.clone();
  while (currentTime.isBefore(endTime)) {
    slots.push({
      timeStart: currentTime.format("h:mma"),
      timeEnd: currentTime.add(interval, "minute").format("h:mma"),
    });
    // Move to the next slot
    currentTime = startTime.add(slots.length * interval, "minute");
  }

  return slots;
}

// Get available slots - simplified version of Preview.js logic
function getAvailableSlots(
  availabilityData,
  selectedDate,
  eventDuration,
  existingBookings = []
) {
  if (!availabilityData || !availabilityData.days) {
    console.log("No availability data or days");
    return [];
  }

  const dayOfWeek = selectedDate.format("dddd");
  const selectedDateStr = selectedDate.format("YYYY-MM-DD");
  const interval = parseInt(eventDuration || 30);

  console.log("=== getAvailableSlots Debug ===");
  console.log("dayOfWeek:", dayOfWeek);
  console.log("selectedDateStr:", selectedDateStr);
  console.log("availabilityData.days:", availabilityData.days);
  console.log("interval:", interval);

  // Check if day is in available days
  if (!availabilityData.days.includes(dayOfWeek)) {
    console.log("Day not in available days");
    return [];
  }

  // Check for an override
  const override = availabilityData.overrides?.find(
    (o) => dayjs(o.date).format("YYYY-MM-DD") === selectedDateStr
  );

  console.log("Override found:", override);

  let timeSlots = [];

  if (override && override.isUnavailable) {
    console.log("Date is marked as unavailable in override");
    return [];
  }

  if (override && !override.isUnavailable && override.timeSlots?.length > 0) {
    // Generate time slots for each entry in override.timeSlots
    console.log("Using override time slots:", override.timeSlots);
    override.timeSlots.forEach((slot) => {
      const slots = generateTimeSlots(slot.timeStart, slot.timeEnd, interval);
      timeSlots = [...timeSlots, ...slots];
    });
  } else {
    // First, check if dayTimeSlots is available (new structure with multiple slots per day)
    const dayTimeSlotEntry = availabilityData.dayTimeSlots?.find(
      (dts) => dts.day === dayOfWeek
    );

    if (dayTimeSlotEntry && dayTimeSlotEntry.slots?.length > 0) {
      // Use all slots from dayTimeSlots for this specific day
      console.log(
        `Using dayTimeSlots for ${dayOfWeek}:`,
        dayTimeSlotEntry.slots
      );
      dayTimeSlotEntry.slots.forEach((slot) => {
        const slots = generateTimeSlots(slot.timeStart, slot.timeEnd, interval);
        timeSlots = [...timeSlots, ...slots];
      });
    } else if (availabilityData.timeSlots?.length > 0) {
      // Fallback to old timeSlots structure (single slot per day or shared slots)
      // Generate time slots for each entry in the timeSlots array
      console.log(
        `Using legacy timeSlots for ${dayOfWeek}:`,
        availabilityData.timeSlots
      );
      availabilityData.timeSlots.forEach((slot) => {
        const slots = generateTimeSlots(slot.timeStart, slot.timeEnd, interval);
        timeSlots = [...timeSlots, ...slots];
      });
    }
  }

  console.log("Generated time slots:", timeSlots);

  // Filter out past times if selected date is today
  const now = dayjs();
  if (selectedDate.isSame(now, "day")) {
    const currentMinutes = now.hour() * 60 + now.minute();
    timeSlots = timeSlots.filter((slot) => {
      // Parse slot time
      const match = slot.timeStart.match(/(\d+):(\d+)(am|pm)/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3].toLowerCase();
        if (period === "pm" && hours !== 12) hours += 12;
        if (period === "am" && hours === 12) hours = 0;
        const slotMinutes = hours * 60 + minutes;
        return slotMinutes > currentMinutes;
      }
      return true;
    });
    console.log("After filtering past times:", timeSlots);
  }

  // Filter out slots that conflict with existing bookings
  // Exclude canceled, cancelled (UK spelling), and rejected bookings - their slots should be available
  if (existingBookings?.length > 0) {
    const dateBookings = existingBookings.filter(
      (b) =>
        b.date === selectedDateStr &&
        b.status !== "canceled" &&
        b.status !== "cancelled" &&
        b.status !== "rejected"
    );

    if (dateBookings.length > 0) {
      console.log("Existing bookings on this date:", dateBookings);

      timeSlots = timeSlots.filter((slot) => {
        // Parse slot start time
        const slotMatch = slot.timeStart.match(/(\d+):(\d+)(am|pm)/i);
        if (!slotMatch) return true;

        let slotHours = parseInt(slotMatch[1], 10);
        const slotMinutes = parseInt(slotMatch[2], 10);
        const slotPeriod = slotMatch[3].toLowerCase();
        if (slotPeriod === "pm" && slotHours !== 12) slotHours += 12;
        if (slotPeriod === "am" && slotHours === 12) slotHours = 0;
        const slotStartMinutes = slotHours * 60 + slotMinutes;
        const slotEndMinutes = slotStartMinutes + interval;

        return !dateBookings.some((booking) => {
          // Parse booking times
          const parseBookingTime = (timeStr) => {
            const match = timeStr.match(/(\d+):(\d+)\s*(am|pm)?/i);
            if (match) {
              let hours = parseInt(match[1], 10);
              const minutes = parseInt(match[2], 10);
              const period = match[3]?.toLowerCase();
              if (period === "pm" && hours !== 12) hours += 12;
              if (period === "am" && hours === 12) hours = 0;
              return hours * 60 + minutes;
            }
            return 0;
          };

          const bookingStart = parseBookingTime(booking.timeStart);
          const bookingEnd = parseBookingTime(booking.timeEnd);

          // Check for overlap
          const hasOverlap =
            slotStartMinutes < bookingEnd && bookingStart < slotEndMinutes;
          return hasOverlap;
        });
      });
      console.log("After filtering conflicts:", timeSlots);
    }
  }

  return timeSlots;
}

// Dialogs component for Bookings page
function BookingDialogs({
  // Kill link dialog props
  killLinkDialog,
  setKillLinkDialog,
  confirmKillLink,
  killingLink,

  // Cancel dialog props
  cancelDialog,
  setCancelDialog,

  // Reschedule dialog props
  rescheduleDialog,
  setRescheduleDialog,
  rescheduleDate,
  setRescheduleDate,
  rescheduleTime,
  setRescheduleTime,
  rescheduleTimezone,
  setRescheduleTimezone,

  // Request reschedule dialog props
  requestRescheduleDialog,
  setRequestRescheduleDialog,

  // Delete dialog props
  deleteDialog,
  setDeleteDialog,
  selectedBookingIds = [],

  // Common props
  sendingEmail,
  confirmCancelBooking,
  confirmRescheduleBooking,
  confirmRequestReschedule,
  confirmDeleteBooking,

  // Availability props
  availabilityData,
  loadingAvailability,

  // Event type data for duration
  eventTypeData,

  // Existing bookings for conflict check
  existingBookings = [],
}) {
  // Local state for calendar
  const [calendarDate, setCalendarDate] = useState(dayjs());
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);

  // Reset calendar when dialog opens
  useEffect(() => {
    if (rescheduleDialog && rescheduleDate) {
      setCalendarDate(dayjs(rescheduleDate));
      setSelectedTime(
        rescheduleTime ? dayjs(rescheduleTime).format("h:mma") : null
      );
    }
  }, [rescheduleDialog, rescheduleDate, rescheduleTime]);

  // Generate available slots when date or availability changes
  useEffect(() => {
    if (!rescheduleDialog) return;

    // Don't generate slots while still loading
    if (loadingAvailability) {
      console.log("Still loading availability, skipping slot generation");
      return;
    }

    const selectedDateForSlots = rescheduleDate
      ? dayjs(rescheduleDate)
      : calendarDate;

    console.log("=== Generating slots ===");
    console.log("loadingAvailability:", loadingAvailability);
    console.log("availabilityData:", availabilityData);
    console.log(
      "selectedDateForSlots:",
      selectedDateForSlots.format("YYYY-MM-DD")
    );
    console.log("eventTypeData:", eventTypeData);
    console.log("eventTypeData?.duration:", eventTypeData?.duration);

    if (availabilityData && selectedDateForSlots) {
      const duration = eventTypeData?.duration || 30;
      console.log("Using duration:", duration);
      const slots = getAvailableSlots(
        availabilityData,
        selectedDateForSlots,
        duration,
        existingBookings
      );
      console.log("Final slots generated:", slots.length, "slots");
      setAvailableSlots(slots);
    } else {
      console.log(
        "Missing data - clearing slots. availabilityData:",
        !!availabilityData,
        "selectedDateForSlots:",
        !!selectedDateForSlots
      );
      setAvailableSlots([]);
    }
  }, [
    rescheduleDialog,
    calendarDate,
    rescheduleDate,
    availabilityData,
    eventTypeData,
    existingBookings,
    loadingAvailability,
  ]);

  // Check if a date should be disabled
  const isDateDisabled = (date) => {
    // Can't select past dates
    if (date.isBefore(dayjs(), "day")) return true;

    if (!availabilityData) return false;

    const dayOfWeek = date.format("dddd");
    const dateStr = date.format("YYYY-MM-DD");

    // Check for override
    const override = availabilityData.overrides?.find(
      (o) => dayjs(o.date).format("YYYY-MM-DD") === dateStr
    );

    if (override) {
      if (override.isUnavailable) return true;
      if (override.timeSlots?.length > 0) return false;
    }

    // Check if day of week is available
    return !availabilityData.days?.includes(dayOfWeek);
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    if (!isDateDisabled(date)) {
      setCalendarDate(date);
      setRescheduleDate(date);
      setSelectedTime(null); // Reset time when date changes
      setRescheduleTime(null);
    }
  };

  // Handle time selection
  const handleTimeSelect = (timeStr) => {
    setSelectedTime(timeStr);
    // Convert to dayjs for the parent component
    const timeParts = timeStr.match(/(\d+):(\d+)(am|pm)/i);
    if (timeParts) {
      let hours = parseInt(timeParts[1], 10);
      const minutes = parseInt(timeParts[2], 10);
      const period = timeParts[3].toLowerCase();
      if (period === "pm" && hours !== 12) hours += 12;
      if (period === "am" && hours === 12) hours = 0;
      setRescheduleTime(dayjs().hour(hours).minute(minutes));
    }
  };

  // Navigate months
  const prevMonth = () => setCalendarDate(calendarDate.subtract(1, "month"));
  const nextMonth = () => setCalendarDate(calendarDate.add(1, "month"));

  // Render the calendar
  const renderCalendar = () => {
    const daysInMonth = calendarDate.daysInMonth();
    const firstDay = calendarDate.startOf("month").day();
    const days = [];
    const weekdays = ["S", "M", "T", "W", "T", "F", "S"];

    // Create days for current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = calendarDate.date(i);
      const isToday =
        date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
      const isSelected =
        rescheduleDate &&
        date.format("YYYY-MM-DD") ===
          dayjs(rescheduleDate).format("YYYY-MM-DD");
      const isDisabled = isDateDisabled(date);

      days.push({
        date: i,
        isToday,
        isSelected,
        isDisabled,
        fullDate: date,
      });
    }

    // Add empty cells before first day
    const emptyCellsBefore = Array(firstDay).fill({
      date: null,
      isCurrentMonth: false,
    });
    const allDays = [...emptyCellsBefore, ...days];

    return (
      <div>
        <div style={styles.monthHeader}>
          <IconButton onClick={prevMonth} size="small" sx={styles.navButton}>
            <ChevronLeft />
          </IconButton>
          <h2 style={styles.monthTitle}>{calendarDate.format("MMMM YYYY")}</h2>
          <IconButton onClick={nextMonth} size="small" sx={styles.navButton}>
            <ChevronRight />
          </IconButton>
        </div>

        <div style={styles.weekdaysGrid}>
          {weekdays.map((day, index) => (
            <div key={index} style={styles.weekday}>
              {day}
            </div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {allDays.map((day, index) => (
            <div key={index} style={styles.dayCell}>
              {day.date && (
                <button
                  style={{
                    ...styles.dayButton,
                    ...(day.isSelected ? styles.dayButtonSelected : {}),
                    ...(day.isToday && !day.isSelected
                      ? styles.dayButtonToday
                      : {}),
                    ...(day.isDisabled ? styles.dayButtonUnavailable : {}),
                  }}
                  onClick={() => handleDateSelect(day.fullDate)}
                  disabled={day.isDisabled}
                >
                  {day.date}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Cancel Booking Dialog */}
      <Dialog
        open={cancelDialog}
        onClose={() => !sendingEmail && setCancelDialog(false)}
      >
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this booking? This will mark the
            booking as canceled and notify the attendee via email. Canceled
            bookings will still appear in your booking history with a "Canceled"
            status.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialog(false)}
            disabled={sendingEmail}
          >
            No, Keep Booking
          </Button>
          <Button
            onClick={confirmCancelBooking}
            color="error"
            variant="contained"
            disabled={sendingEmail}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : null}
          >
            {sendingEmail ? "Canceling..." : "Yes, Cancel Booking"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog - Calendar Style */}
      <Dialog
        open={rescheduleDialog}
        onClose={() => !sendingEmail && setRescheduleDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <DialogTitle>
          Reschedule Booking
          {rescheduleDate && selectedTime && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              New time: {dayjs(rescheduleDate).format("dddd, MMMM D, YYYY")} at{" "}
              {selectedTime}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          {loadingAvailability ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 400,
              }}
            >
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading availability...</Typography>
            </Box>
          ) : (
            <div style={styles.flexRow}>
              {/* Calendar Section */}
              <div style={styles.calendarSection}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Select a Date
                </Typography>
                {renderCalendar()}

                {/* Timezone selector */}
                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={rescheduleTimezone || "UTC"}
                      onChange={(e) => setRescheduleTimezone(e.target.value)}
                      label="Timezone"
                      disabled={sendingEmail}
                    >
                      <MenuItem value="UTC">UTC</MenuItem>
                      <MenuItem value="America/New_York">
                        Eastern Time (ET)
                      </MenuItem>
                      <MenuItem value="America/Chicago">
                        Central Time (CT)
                      </MenuItem>
                      <MenuItem value="America/Denver">
                        Mountain Time (MT)
                      </MenuItem>
                      <MenuItem value="America/Los_Angeles">
                        Pacific Time (PT)
                      </MenuItem>
                      <MenuItem value="Europe/London">London (GMT)</MenuItem>
                      <MenuItem value="Europe/Paris">Paris (CET)</MenuItem>
                      <MenuItem value="Asia/Kolkata">India (IST)</MenuItem>
                      <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
                      <MenuItem value="Australia/Sydney">
                        Sydney (AEST)
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {availabilityData?.days && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Available days: {availabilityData.days.join(", ")}
                    </Typography>
                  </Alert>
                )}
              </div>

              {/* Time Slots Section */}
              <div style={styles.timeSlotsSection}>
                <Typography style={styles.timelineHeader}>
                  {rescheduleDate
                    ? `Available Times for ${dayjs(rescheduleDate).format(
                        "MMM D"
                      )}`
                    : "Select a date first"}
                </Typography>

                <div style={styles.timeSlotsContainer}>
                  {!rescheduleDate ? (
                    <div style={styles.noSlots}>
                      <Typography color="text.secondary">
                        Please select a date to see available times
                      </Typography>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div style={styles.timeSlotsList}>
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          style={{
                            ...styles.timeSlotButton,
                            ...(selectedTime === slot.timeStart
                              ? styles.timeSlotButtonSelected
                              : {}),
                          }}
                          onClick={() => handleTimeSelect(slot.timeStart)}
                          disabled={sendingEmail}
                        >
                          {slot.timeStart}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.noSlots}>
                      <Typography color="text.secondary" gutterBottom>
                        No available slots
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Try selecting a different date
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRescheduleDialog(false)}
            disabled={sendingEmail}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRescheduleBooking}
            color="primary"
            variant="contained"
            disabled={
              !rescheduleDate ||
              !selectedTime ||
              sendingEmail ||
              loadingAvailability
            }
            startIcon={sendingEmail ? <CircularProgress size={20} /> : null}
          >
            {sendingEmail ? "Rescheduling..." : "Confirm Reschedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Reschedule Dialog */}
      <Dialog
        open={requestRescheduleDialog}
        onClose={() => !sendingEmail && setRequestRescheduleDialog(false)}
      >
        <DialogTitle>Request Reschedule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will send an email to the attendee requesting them to
            reschedule their booking. They will be provided with a link to
            select a new time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRequestRescheduleDialog(false)}
            disabled={sendingEmail}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRequestReschedule}
            color="primary"
            variant="contained"
            disabled={sendingEmail}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : null}
          >
            {sendingEmail ? "Sending..." : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Booking Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>
          {selectedBookingIds.length > 1
            ? `Delete ${selectedBookingIds.length} Bookings?`
            : "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedBookingIds.length > 1
              ? `Are you sure you want to permanently delete ${selectedBookingIds.length} bookings? This action cannot be undone. All selected bookings will be completely removed from the system and will not appear in your booking history.`
              : "Are you sure you want to permanently delete this booking? This action cannot be undone. The booking will be completely removed from the system and will not appear in your booking history."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteBooking}
            color="error"
            variant="contained"
          >
            {selectedBookingIds.length > 1
              ? `Delete ${selectedBookingIds.length} Bookings`
              : "Delete Booking"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kill Meeting Link Dialog */}
      <Dialog
        open={killLinkDialog || false}
        onClose={() => !killingLink && setKillLinkDialog(false)}
      >
        <DialogTitle>Kill Meeting Link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently revoke the meeting link and close the MiroTalk
            room. Participants will no longer be able to join. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setKillLinkDialog(false)}
            disabled={killingLink}
          >
            No, Keep Link
          </Button>
          <Button
            onClick={confirmKillLink}
            color="error"
            variant="contained"
            disabled={killingLink}
            startIcon={killingLink ? <CircularProgress size={20} /> : null}
          >
            {killingLink ? "Killing Link..." : "Yes, Kill Link"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BookingDialogs;
